import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Wizard, WizardStep, ValidatedOptions, Alert } from '@patternfly/react-core';
import { ClusterPicker } from '@app/GranularDeploy/components/ClusterPicker';
import { Step1Footer, Step3Footer, StepResultsFooter } from '@app/GranularDeploy/components/WizardFooters';
import { CLUSTERS } from '@app/config';
import { ConfigureStep, ConfigureFormValues } from './ConfigureStep';
import { OverlayCandidatesStep } from './OverlayCandidatesStep';
import { ReviewStep } from './ReviewStep';
import { DeployStep, SuiteTriggerState } from './DeployStep';
import { TestingStep } from './TestingStep';
import { useOverlayCandidates } from '../hooks/usePreviewEnv';
import { usePreviewDeploy } from '../hooks/usePreviewEnv';
import { useSessionInUse } from '../hooks/usePreviewEnv';
import { useDebounce } from '@app/GranularDeploy/hooks/useDebounce';
import { PreviewService, InfraService, MirrordOverrides, DbBranchParamValue } from '../helpers/apiHelpers';
import { BranchEntry } from './ConfigureStep';

// Mirror the same cluster bootstrap used by CreateEnvWizard
globalThis.cluster_selected_value = localStorage.getItem('cluster_selected');
if (
  globalThis.cluster_selected_value == null ||
  !Object.keys(CLUSTERS).includes(globalThis.cluster_selected_value)
) {
  globalThis.cluster_selected_value = Object.values(CLUSTERS)[0].CLUSTER_NAME;
  localStorage.setItem('cluster_selected', globalThis.cluster_selected_value);
  window.location.reload();
}
globalThis.cluster = CLUSTERS[globalThis.cluster_selected_value];

const TTL_PATTERN = /^\d+[hd]$/;

const sanitizeMirrordOverrides = (overrides: MirrordOverrides): MirrordOverrides | undefined => {
  const feature = overrides.feature;
  if (!feature) return undefined;

  const rawEnv = feature.env?.override ?? {};
  const cleanEnv: Record<string, string> = {};
  for (const [key, value] of Object.entries(rawEnv)) {
    const trimmed = key.trim();
    if (!trimmed) continue;
    cleanEnv[trimmed] = value;
  }

  const env = Object.keys(cleanEnv).length > 0
    ? { ...(feature.env ?? {}), override: cleanEnv }
    : undefined;

  // Strip db_branches entries that have empty connection params, and clean up params
  const rawDbBranches = feature.db_branches;
  const dbBranches = rawDbBranches?.map(entry => {
    const cleanParams: Record<string, DbBranchParamValue> = {};
    for (const [k, v] of Object.entries(entry.connection?.params ?? {})) {
      if (Array.isArray(v)) {
        const trimmed = v.map(name => name.trim()).filter(Boolean);
        if (trimmed.length > 0) cleanParams[k] = trimmed;
      } else if (typeof v === 'object' && v !== null) {
        if (!v.env_var_name.trim()) continue;
        cleanParams[k] = { env_var_name: v.env_var_name.trim(), value_pattern: v.value_pattern };
      } else if (typeof v === 'string' && v.trim()) {
        cleanParams[k] = v.trim();
      }
    }
    const connection = Object.keys(cleanParams).length > 0 ? { params: cleanParams } : undefined;

    // Clean up migrations: trim strings, drop empty command/args/env entries, drop the whole
    // field if there's no image (container flavor is the only one that exists now).
    let migrations = entry.migrations;
    if (migrations) {
      const image = migrations.image?.trim() ?? '';
      const command = migrations.command?.map(c => c.trim()).filter(Boolean);
      const args = migrations.args?.map(a => a.trim()).filter(Boolean);
      const cleanEnv: Record<string, string> = {};
      for (const [k, v] of Object.entries(migrations.env ?? {})) {
        const trimmed = k.trim();
        if (trimmed) cleanEnv[trimmed] = v;
      }
      migrations = image
        ? {
            flavor: 'container',
            image,
            ...(command && command.length > 0 ? { command } : {}),
            ...(args && args.length > 0 ? { args } : {}),
            ...(Object.keys(cleanEnv).length > 0 ? { env: cleanEnv } : {}),
          }
        : undefined;
    }

    // `copy.mode: "schema"` skips row data entirely — including the app's own embedded
    // flyway-core migration-history table — so on boot it sees an apparently-unmigrated schema
    // and replays every migration from scratch, failing on "relation already exists". Auto-
    // including `flyway_schema_history` via `copy.tables` fixes this; only needed under "schema"
    // mode — "all" already copies the row data. Applies regardless of whether an explicit
    // `migrations` block is attached, since embedded flyway-core runs on every boot either way.
    const copy = (entry.type === 'postgres' && entry.copy?.mode === 'schema')
      ? { ...entry.copy, tables: { ...entry.copy?.tables, flyway_schema_history: {} } }
      : entry.copy;

    return { ...entry, connection, copy, migrations };
  });

  const splitQueues = feature.split_queues;

  if (!env && !dbBranches && !splitQueues) return undefined;

  return {
    ...overrides,
    feature: {
      ...feature,
      ...(env ? { env } : { env: undefined }),
      ...(dbBranches ? { db_branches: dbBranches } : {}),
    },
  };
};

const validateOverridesBeforeDeploy = (overrides: Record<string, MirrordOverrides>): { error: string | null; warning: string | null } => {
  const paramFilled = (v: DbBranchParamValue | undefined) =>
    Array.isArray(v) ? v.some(name => name.trim().length > 0)
    : typeof v === 'object' && v !== null ? v.env_var_name.trim().length > 0
    : typeof v === 'string' && v.trim().length > 0;

  for (const [svcName, ov] of Object.entries(overrides)) {
    const feature = ov.feature;
    if (!feature) continue;
    if (feature.db_branches) {
      for (const entry of feature.db_branches) {
        if (!entry.id?.trim()) {
          return { error: `DB branching for "${svcName}": Branch ID is required before deploying`, warning: null };
        }
        const params = entry.connection?.params ?? {};
        const hasAnyParam = Object.values(params).some(paramFilled);
        if (!hasAnyParam) {
          return { error: `DB branching for "${svcName}": At least one connection parameter (Host, User, Password, or Database env var) is required. mirrord uses these to locate the source DB in the running pod.`, warning: null };
        }
        if (entry.migrations && !entry.name?.trim()) {
          return { error: `DB branching for "${svcName}": Database Name is required when Schema Migrations are enabled`, warning: null };
        }
        if (entry.migrations && !entry.migrations.image?.trim()) {
          return { error: `Schema Migrations for "${svcName}": Image is required`, warning: null };
        }
      }
    }
    if (feature.split_queues) {
      for (const key of Object.keys(feature.split_queues)) {
        if (!key.trim()) {
          return { error: `Kafka splitting for "${svcName}": Topic name is required before deploying`, warning: null };
        }
      }
    }
  }

  return { error: null, warning: null };
};

// ---------------------------------------------------------------------------
// Per-service DB/Kafka config persistence (localStorage, keyed by service name)
// ---------------------------------------------------------------------------
type PersistedServiceConfig = {
  db_branches?: import('../helpers/apiHelpers').DbBranchEntry[];
  split_queues?: Record<string, import('../helpers/apiHelpers').KafkaSplitEntry>;
};

const svcStorageKey = (name: string) => `preview-svc-config:${name}`;

function loadSvcConfig(name: string): PersistedServiceConfig {
  try {
    const raw = localStorage.getItem(svcStorageKey(name));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveSvcConfig(name: string, config: PersistedServiceConfig) {
  try {
    if (Object.keys(config).length) {
      localStorage.setItem(svcStorageKey(name), JSON.stringify(config));
    }
  } catch {}
}

export const PreviewEnvWizard: React.FC = () => {
  const [authError, setAuthError] = useState<string | null>(null);

  const [config, setConfig] = useState<ConfigureFormValues>({
    inputMode: 'pr',
    entries: [],
    resolvedBranches: [],
    namespace: '',
    previewTag: '',
    ttl: '4h',
    forceOverride: false,
    regressionTestsEnabled: false,
    regressionTestSelections: {},
  });

  const [regressionTriggerResults, setRegressionTriggerResults] = useState<Record<string, SuiteTriggerState>>({});
  const [regressionTriggerStarted, setRegressionTriggerStarted] = useState(false);

  const [selectedServices, setSelectedServices] = useState<PreviewService[]>([]);
  const [selectedInfra, setSelectedInfra] = useState<PreviewService[]>([]);
  const [dbBranch, setDbBranch] = useState<string>('');
  const [serviceOverrides, setServiceOverrides] = useState<Record<string, MirrordOverrides>>({});
  const [deployValidationError, setDeployValidationError] = useState<string | null>(null);
  const [deployValidationWarning, setDeployValidationWarning] = useState<string | null>(null);
  // True while any service's automatic schema-migrations check (GitHub-diff lookup) is still in
  // flight — blocks advancing/deploying so the migrations config can't be silently dropped by the
  // user moving faster than the async check.
  const [migrationsChecksPending, setMigrationsChecksPending] = useState(false);

  const debouncedNamespace = useDebounce(config.namespace, 400);
  const debouncedDbBranch = useDebounce(dbBranch || config.resolvedBranches[0] || '', 600);

  // All resolved (non-empty) branches across all entries
  const resolvedBranches = config.resolvedBranches.filter((b): b is string => !!b);
  const debouncedBranchesKey = useDebounce(resolvedBranches.join(','), 600);
  const debouncedBranches = debouncedBranchesKey ? debouncedBranchesKey.split(',') : [];

  // PR URLs from step 1 entries — used to pre-select matching services in step 2
  const prUrls = config.entries
    .filter((e): e is { kind: 'pr'; url: string } => e.kind === 'pr')
    .map((e) => e.url);
  const debouncedPrUrlsKey = useDebounce(prUrls.join(','), 600);
  const debouncedPrUrls = debouncedPrUrlsKey ? debouncedPrUrlsKey.split(',') : [];

  // Clear stale selections whenever the branches or namespace changes
  useEffect(() => {
    setSelectedServices([]);
    setSelectedInfra([]);
    setDbBranch(resolvedBranches[0] ?? '');
    setServiceOverrides({});
  }, [debouncedBranchesKey, debouncedNamespace]);

  // Persist DB/Kafka overrides per service (not env var overrides — those are ephemeral).
  // Redis branches are excluded — Redis is driven entirely by the per-session feature-flag
  // toggle in OverlayCandidatesStep and must never be silently restored on a later visit.
  // Migration container env is stripped before persisting — it's manually typed in and can carry
  // real credentials (see db-branching-env-var-audit.md finding #7); it must not sit in
  // localStorage indefinitely just because DB branching itself is meant to be remembered. Users
  // re-enter it each session, same as if it had never been saved.
  useEffect(() => {
    for (const [svcName, ov] of Object.entries(serviceOverrides)) {
      const feature = ov.feature;
      const toSave: PersistedServiceConfig = {};
      const persistableBranches = feature?.db_branches
        ?.filter(b => b.type !== 'redis')
        .map(b => (b.migrations?.flavor === 'container' && b.migrations.env
          ? { ...b, migrations: { ...b.migrations, env: {} } }
          : b));
      if (persistableBranches?.length) toSave.db_branches = persistableBranches;
      if (feature?.split_queues && Object.keys(feature.split_queues).length) toSave.split_queues = feature.split_queues;
      saveSvcConfig(svcName, toSave);
    }
  }, [serviceOverrides]);


  const debouncedTag = useDebounce(config.previewTag, 500);

  // Narrows overlay-candidates to just the PR-resolved service(s) instead of every workload in
  // the namespace that happens to share the same branch — only applies in PR input mode, since a
  // manually-typed branch name isn't tied to one specific service.
  const onlyPr = config.inputMode === 'pr';

  const { candidates, baselineCandidates, infraServices, infraDepsMap, serviceDepsMap, suggestedServices, loading: candidatesLoading, error: candidatesError } =
    useOverlayCandidates(debouncedBranches, debouncedNamespace, debouncedDbBranch, debouncedPrUrls, onlyPr);

  // Restore saved DB/Kafka config on first selection, excluding Redis and any type the live
  // template now overrides (a stale draft must never shadow a newer template override).
  useEffect(() => {
    const candidatesByName = new Map(candidates.map(c => [c.application, c]));
    const updates: Record<string, MirrordOverrides> = {};
    for (const svc of selectedServices) {
      if (serviceOverrides[svc.name]) continue;
      const saved = loadSvcConfig(svc.name);
      const templateOverrides = candidatesByName.get(svc.name)?.dbBranchOverrides ?? {};
      const restorableBranches = saved.db_branches?.filter(b => b.type !== 'redis' && !templateOverrides[b.type]);
      if (restorableBranches?.length || saved.split_queues) {
        updates[svc.name] = {
          feature: {
            ...(restorableBranches?.length ? { db_branches: restorableBranches } : {}),
            ...(saved.split_queues ? { split_queues: saved.split_queues } : {}),
          },
        };
      }
    }
    if (Object.keys(updates).length) {
      setServiceOverrides(prev => ({ ...prev, ...updates }));
    }
  }, [selectedServices, candidates]);

  // Auto-select services suggested by PR content.
  // Resets whenever branches, namespace, or the PR URL list changes.
  // When new PRs are added the effect merges new suggestions into the existing selection
  // rather than replacing it, so manually-selected services are preserved.
  const appliedSuggestionsRef = useRef<string>('');
  useEffect(() => {
    appliedSuggestionsRef.current = '';
  }, [debouncedBranchesKey, debouncedNamespace, debouncedPrUrlsKey]);
  useEffect(() => {
    if (candidatesLoading || candidates.length === 0 || suggestedServices.length === 0) return;
    const key = suggestedServices.slice().sort().join(',');
    if (appliedSuggestionsRef.current === key) return;
    appliedSuggestionsRef.current = key;
    const toSelect = candidates
      .filter((c) => suggestedServices.includes(c.application) && (c.images?.found_tags?.length ?? 0) > 0)
      .map((c) => {
        const tag = c.images?.found_tags?.[0] ?? '';
        const imageName = c.images?.image_name ?? c.application;
        const image = tag ? `docker.artifactory.intouchhealth.io/tdh/${imageName}:${tag}` : '';
        return { name: c.application, image, type: c.workloadType as 'deployment' | 'statefulset' };
      });
    if (toSelect.length > 0) {
      setSelectedServices((prev) => {
        const existingNames = new Set(prev.map((s) => s.name));
        const newOnes = toSelect.filter((s) => !existingNames.has(s.name));
        return newOnes.length > 0 ? [...prev, ...newOnes] : prev;
      });
    }
  }, [candidates, candidatesLoading, suggestedServices]);

  const { inUse: tagInUse, checking: tagChecking } = useSessionInUse(
    config.namespace,
    debouncedTag,
  );

  const { deploy, isDeploying, jobUrl, deployError, statusMessage, sessionStatus } = usePreviewDeploy();

  const isStep1Valid = useMemo(() => {
    const ttlOk = TTL_PATTERN.test(config.ttl);
    const tagOk = config.previewTag.length > 0 && (tagInUse === false || (tagInUse === true && config.forceOverride));
    const hasAtLeastOneBranch = resolvedBranches.length > 0;
    return (
      hasAtLeastOneBranch &&
      config.namespace.length > 0 &&
      tagOk &&
      ttlOk
    );
  }, [config, tagInUse, resolvedBranches]);

  // Computed alongside isStep2Valid (same checks, same order) so the Next button's disabled
  // state always has a matching, specific explanation instead of failing silently.
  const step2DisabledReason = useMemo(() => {
    if (selectedServices.length === 0) return 'Select at least one service to deploy.';
    if (migrationsChecksPending) return 'Waiting for a schema-migrations check to finish…';
    const candidateTagsMap = new Map<string, string[]>();
    candidates.forEach(c => candidateTagsMap.set(c.application, c.images?.found_tags ?? []));
    const baselineCandidateNames = new Set(baselineCandidates.map(bc => bc.application));
    for (const svc of selectedServices) {
      if (baselineCandidateNames.has(svc.name)) {
        if (svc.image.length === 0) return `${svc.name} has no image set.`;
        continue;
      }
      const tag = svc.image.split(':').pop() ?? '';
      if (tag.length === 0 || !(candidateTagsMap.get(svc.name) ?? []).includes(tag)) {
        return `${svc.name}'s image tag ("${tag || 'none'}") wasn't found by Ranch — try deselecting and reselecting it.`;
      }
    }
    return null;
  }, [selectedServices, candidates, baselineCandidates, migrationsChecksPending]);

  const isStep2Valid = step2DisabledReason === null;

  const handleDeploy = async () => {
    if (migrationsChecksPending) {
      const message = 'A schema-migrations check is still in progress — please wait a moment and try again.';
      setDeployValidationError(message);
      setDeployValidationWarning(null);
      throw new Error(message);
    }
    // Only validate overrides for services actually being deployed — a stale override left over
    // from a deselected service (should be cleared at the source when unselecting, but this is a
    // defensive backstop) must never block deploy for a service the payload below won't even include.
    const selectedNames = new Set(selectedServices.map(s => s.name));
    const relevantOverrides = Object.fromEntries(
      Object.entries(serviceOverrides).filter(([svcName]) => selectedNames.has(svcName)),
    );
    const { error: validationError, warning: validationWarning } = validateOverridesBeforeDeploy(relevantOverrides);
    if (validationError) {
      setDeployValidationError(validationError);
      setDeployValidationWarning(null);
      throw new Error(validationError);
    }
    setDeployValidationError(null);
    setDeployValidationWarning(validationWarning);
    // DEVEX-819: carry each service's Kafka topic catalog through for ensureKafkaTopicCatalog().
    const kafkaTopicsByName = new Map([...candidates, ...baselineCandidates].map((c) => [c.application, c.kafkaTopics]));

    // Merge per-service overrides into the services array before sending
    const servicesWithOverrides: PreviewService[] = selectedServices.map((svc) => {
      const overrides = serviceOverrides[svc.name];
      const kafkaTopicsCatalog = kafkaTopicsByName.get(svc.name);
      const withCatalog = kafkaTopicsCatalog ? { ...svc, kafkaTopicsCatalog } : svc;
      if (!overrides) return withCatalog;
      const sanitized = sanitizeMirrordOverrides(overrides);
      if (!sanitized) return withCatalog;
      return { ...withCatalog, mirrordOverrides: sanitized };
    });
    // Every companion name (tas-sidekiq, tas-karafka, ...) the wizard ever surfaced as a candidate
    // for the services being deployed — sent even when empty, so the server can tell "the user
    // consciously left this companion unchecked" apart from "this client predates companions and
    // never saw them at all" (see COMPANION_DEPLOYMENTS in routes/previewenv.ts). Built from the
    // full `candidates` list (not just selectedServices) so a deselected companion is still
    // reported as considered.
    const companionsConsidered = [...new Set(candidates.flatMap((c) => c.companions ?? []))];
    const payload: Parameters<typeof deploy>[0] = {
      environment: globalThis.cluster_selected_value,
      namespace: config.namespace,
      previewKey: config.previewTag,
      ttl: config.ttl,
      services: servicesWithOverrides,
      companionsConsidered,
      ...(config.forceOverride ? { force: true } : {}),
    };
    // Infra services (DB, Kafka, etc.) are baseline — never redeployed.
    // DB branching and Kafka splitting are handled via mirrordOverrides on the app services.
    // payload.infra is intentionally omitted.
    await deploy(payload);
  };

  return (
    <div style={{ position: 'relative', margin: '-28px -32px', height: 'calc(100% + 56px)' }}>
      {authError && (
        <Alert variant="danger" title={authError} isInline style={{ width: '100%', marginBottom: '1rem', padding: '0 24px' }} />
      )}
      {deployValidationError && (
        <Alert variant="danger" title={deployValidationError} isInline style={{ width: '100%', marginBottom: '1rem', padding: '0 24px' }} />
      )}
      {deployValidationWarning && (
        <Alert variant="warning" title={deployValidationWarning} isInline style={{ width: '100%', marginBottom: '1rem', padding: '0 24px' }} />
      )}

      <Wizard
        style={{ height: '100%' }}
        header={
          <div className="dx-flex dx-flex--justify-between" style={{ width: '100%', padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontSize: '22px', fontWeight: 600, color: 'var(--text-primary)' }}>Preview Environment</div>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '6px' }}>Configure and launch a mirrord preview session</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Target Cluster</span>
              <ClusterPicker onAuthError={setAuthError} />
            </div>
          </div>
        }
      >
        {/* Step 1 — Configure */}
        <WizardStep
          name="Configure"
          id="preview-step-configure"
          footer={
            <Step1Footer
              isNextDisabled={!isStep1Valid}
              authError={null}
              setAuthError={() => {}}
              hideBack
            />
          }
        >
          <ConfigureStep values={config} onChange={setConfig} />
        </WizardStep>

        {/* Step 2 — Overlay Candidates */}
        <WizardStep
          name="Overlay Candidates"
          id="preview-step-candidates"
          footer={
            <Step1Footer
              isNextDisabled={!isStep2Valid}
              disabledReason={step2DisabledReason}
              authError={null}
              setAuthError={() => {}}
            />
          }
        >
          <OverlayCandidatesStep
            previewTag={config.previewTag}
            candidates={candidates}
            baselineCandidates={baselineCandidates}
            loading={candidatesLoading}
            error={candidatesError}
            selectedServices={selectedServices}
            onSelectionChange={setSelectedServices}
            infraServices={infraServices}
            infraDepsMap={infraDepsMap}
            serviceDepsMap={serviceDepsMap}
            selectedInfra={selectedInfra}
            onInfraChange={setSelectedInfra}
            dbBranch={dbBranch || resolvedBranches[0] || ''}
            onDbBranchChange={setDbBranch}
            serviceOverrides={serviceOverrides}
            onOverridesChange={setServiceOverrides}
            suggestedServices={suggestedServices}
            onPendingMigrationsChecksChange={setMigrationsChecksPending}
          />
        </WizardStep>

        {/* Step 3 — Review */}
        <WizardStep
          name="Review"
          id="preview-step-review"
          footer={
            <Step3Footer
              onDeploy={handleDeploy}
              disableDeploy={isDeploying}
              isDeploying={isDeploying}
            />
          }
        >
          <ReviewStep config={config} selectedServices={selectedServices} selectedInfra={selectedInfra} dbBranch={dbBranch || resolvedBranches[0] || ''} serviceOverrides={serviceOverrides} />
        </WizardStep>

        {/* Step 4 — Deploy status */}
        <WizardStep
          name="Deploy"
          id="preview-step-deploy"
          footer={<StepResultsFooter />}
        >
          <DeployStep
            isDeploying={isDeploying}
            jobUrl={jobUrl}
            deployError={deployError}
            statusMessage={statusMessage}
            previewTag={config.previewTag}
            sessionStatus={sessionStatus}
            namespace={config.namespace}
            environment={globalThis.cluster_selected_value}
            deployedServiceNames={selectedServices.map(svc => svc.name)}
            regressionTestsEnabled={config.regressionTestsEnabled}
            regressionTestSelections={config.regressionTestSelections}
            regressionTriggerStarted={regressionTriggerStarted}
            onRegressionTriggerStarted={() => setRegressionTriggerStarted(true)}
            regressionTriggerResults={regressionTriggerResults}
            onRegressionTriggerResultsChange={setRegressionTriggerResults}
          />
        </WizardStep>

        {/* Step 5 — Testing (regression suites, auto-triggered on deploy success) */}
        <WizardStep
          name="Testing"
          id="preview-step-testing"
          isHidden={!config.regressionTestsEnabled}
          footer={<StepResultsFooter />}
        >
          <TestingStep
            selections={config.regressionTestSelections}
            results={regressionTriggerResults}
          />
        </WizardStep>
      </Wizard>
    </div>
  );
};
